import { Recommendation } from './patientAdvisory';

export enum CareGapTextField {
  // all
  dateOfService = 'dateOfService',
  locationOfService = 'locationOfService',

  // CT
  conditionName = 'conditionName',
  conditionEvidence = 'conditionEvidence',
  documentCategory = 'documentCategory',
  imagingType = 'imagingType',

  // diabetes exam
  hba1cValue = 'hba1cValue',
  isClosed = 'isClosed',
  retinopathyResultText = 'retinopathyResultText',
  testDescription = 'testDescription',
  urAlbuminCreatinineRatio = 'urAlbuminCreatinineRatio',
  urCreatinine = 'urCreatinine',
  urMicroalbumin = 'urMicroalbumin',

  // pap smear
  hpvResult = 'hpvResult',
  intraepithelialLesionOrMalignancy = 'intraepithelialLesionOrMalignancy',

  // colon cancer screening and mammogram
  impressionAndRecommendation = 'impressionAndRecommendation',
  timeUntilNextVisit = 'timeUntilNextVisit',

  // cervical cancer screening
  locationOfTest = 'locationOfTest',
  result = 'result',
  resultDescription = 'resultDescription',

  // colon cancer screening
  providerName = 'providerName',
  providerSpecialty = 'providerSpecialty',
  screeningType = 'screeningType',

  // hyptertension
  diastolicBloodPressure = 'diastolicBloodPressure',
  systolicBloodPressure = 'systolicBloodPressure',
}

export enum CareGapCategoryField {
  retinopathyPresence = 'retinopathyPresence',
}

export enum CareGapCategoryOptions {
  positive = 'POSITIVE',
  negative = 'NEGATIVE',
  notIndicated = 'NOT_INDICATED',
}

export const careGapKindToTextFields: {
  [key in Recommendation]?: CareGapTextField[];
} = {
  [Recommendation.CT]: [
    CareGapTextField.documentCategory,
    CareGapTextField.dateOfService,
    CareGapTextField.conditionName,
    CareGapTextField.conditionEvidence,
    CareGapTextField.imagingType,
  ],
  [Recommendation.DiabetesEyeExam]: [
    CareGapTextField.dateOfService,
    CareGapTextField.impressionAndRecommendation,
    CareGapTextField.locationOfTest,
    CareGapTextField.screeningType,
    CareGapTextField.testDescription,
  ],
  [Recommendation.DiabetesHbA1cMonitoring]: [CareGapTextField.hba1cValue],
  [Recommendation.DiabetesNephropathyMonitoring]: [
    CareGapTextField.retinopathyResultText,
  ],
  [Recommendation.PreventiveCareBreastCancerScreeningForWomenFrom50To75]: [
    CareGapTextField.impressionAndRecommendation,
    CareGapTextField.timeUntilNextVisit,
  ],
  [Recommendation.PreventiveCareColonCancerScreen]: [
    CareGapTextField.dateOfService,
    CareGapTextField.impressionAndRecommendation,
    CareGapTextField.locationOfTest,
    CareGapTextField.providerName,
    CareGapTextField.providerSpecialty,
    CareGapTextField.resultDescription,
    CareGapTextField.screeningType,
  ],
  [Recommendation.CervicalCancerScreening]: [
    CareGapTextField.dateOfService,
    CareGapTextField.resultDescription,
    CareGapTextField.result,
    CareGapTextField.locationOfTest,
  ],
  [Recommendation.PreventiveCarePapSmear]: [
    CareGapTextField.hpvResult,
    CareGapTextField.intraepithelialLesionOrMalignancy,
  ],
  [Recommendation.Hypertension]: [
    CareGapTextField.dateOfService,
    CareGapTextField.diastolicBloodPressure,
    CareGapTextField.impressionAndRecommendation,
    CareGapTextField.locationOfTest,
    CareGapTextField.screeningType,
    CareGapTextField.systolicBloodPressure,
  ],
};

export const careGapKindToCategoryFields: {
  [key in Recommendation]?: CareGapCategoryField[];
} = {
  [Recommendation.DiabetesEyeExam]: [CareGapCategoryField.retinopathyPresence],
};

export const careGapCategoryFieldToOptions: {
  [key in CareGapCategoryField]: CareGapCategoryOptions[];
} = {
  [CareGapCategoryField.retinopathyPresence]: [
    CareGapCategoryOptions.positive,
    CareGapCategoryOptions.negative,
    CareGapCategoryOptions.notIndicated,
  ],
};

export interface CreateValues {
  patientDocumentId: string;
  careGapKind: Recommendation | null;
  field: CareGapTextField | CareGapCategoryField;
  value: string;
  startIndexInDocument: number | null;
  endIndexInDocument: number | null;
}
export interface CareGapTextFieldValues
  extends Omit<
    CreateValues,
    'field' | 'startIndexInDocument' | 'endIndexInDocument'
  > {
  field: CareGapTextField;
  startIndexInDocument: number;
  endIndexInDocument: number;
}

export interface CareGapCategoryFieldValues
  extends Omit<
    CreateValues,
    'field' | 'startIndexInDocument' | 'startIndexInDocument'
  > {
  field: CareGapCategoryField;
  startIndexInDocument: null;
  endIndexInDocument: null;
}

export const isCareGapTextFieldValue = (
  value: CreateValues,
): value is CareGapTextFieldValues =>
  Object.values(CareGapTextField)
    .map((field) => field.toString())
    .includes(value.field.toString());

export const isCareGapCategoryValue = (
  value: CreateValues,
): value is CareGapCategoryFieldValues =>
  Object.values(CareGapCategoryField)
    .map((field) => field.toString())
    .includes(value.field.toString());
